import React, { useCallback, useEffect, useState } from 'react';
import { db } from '../services/db';
import { PendingReceipt } from '../models/types';
import { isEnrolled } from '../services/enrollmentService';
import { syncPendingReceipts, retryReceipt, discardReceipt, SyncResult } from '../services/receiptQueue';
import '../styles/Modal.css';
import '../styles/ReceiptCapture.css';

interface PendingReceiptsProps {
  onClose: () => void;
}

interface StatusMessage {
  text: string;
  kind: 'success' | 'error' | 'info';
}

/**
 * Turn a drain's result into what an employee should be told.
 *
 * `syncPendingReceipts` can return {delivered:0, failed:0} for three very
 * different reasons (nothing eligible, unenrolled, already draining), which
 * is exactly the ambiguity a "Sending…" button that silently does nothing
 * would paper over (review finding, round 2). `skipped` disambiguates it;
 * this function is the single place that turns that into copy.
 */
const describeResult = (result: SyncResult): StatusMessage | null => {
  if (result.skipped === 'unenrolled') {
    return { text: "This phone isn't set up with the office yet.", kind: 'error' };
  }
  if (result.skipped === 'reentrant') {
    return { text: 'Already sending — try again in a moment.', kind: 'info' };
  }

  const attempted = result.delivered + result.failed;
  if (attempted === 0) {
    // A real drain ran and attempted nothing — but that is two different
    // situations for the employee. If rows were skipped because they are
    // flagged needsAttention, "Sending…" would otherwise flash and land on
    // total silence (review finding); say so instead of returning null.
    if (result.stuck) {
      return {
        text: `${result.stuck} receipt${result.stuck === 1 ? '' : 's'} need${
          result.stuck === 1 ? 's' : ''
        } attention — use Retry on each below.`,
        kind: 'error',
      };
    }
    return null; // genuinely nothing left to attempt
  }

  if (result.failed === 0) return { text: `Sent ${result.delivered} of ${attempted}.`, kind: 'success' };
  if (result.delivered === 0) return { text: 'Nothing sent — still no connection.', kind: 'error' };
  return { text: `Sent ${result.delivered} of ${attempted}. Still no connection for the rest.`, kind: 'info' };
};

/**
 * What is still on this phone.
 *
 * A queue an employee cannot see is a queue that fails silently — the whole
 * risk of storing receipts locally is that nobody notices they never left.
 * This is the receipts equivalent of the pending-entries count.
 */
const PendingReceipts: React.FC<PendingReceiptsProps> = ({ onClose }) => {
  const [rows, setRows] = useState<PendingReceipt[]>([]);
  const [busy, setBusy] = useState(false);
  const [retryingId, setRetryingId] = useState<number | null>(null);
  const [enrolled, setEnrolled] = useState<boolean | null>(null);
  const [status, setStatus] = useState<StatusMessage | null>(null);

  // Never throws: every caller — the mount effect below, and each handler's
  // post-action refresh (handleRetryAll/handleRetry/handleDiscard) — can call
  // this without its own try/catch. `isCancelled` is only supplied by the
  // mount effect, so a read that resolves after this instance has unmounted
  // (the employee closed the modal while it was in flight) skips setRows;
  // the handlers run from a still-mounted event, so they rely on the no-op
  // default.
  const load = useCallback(async (isCancelled: () => boolean = () => false) => {
    try {
      const loaded = await db.pendingReceipts.orderBy('createdAt').toArray();
      if (!isCancelled()) setRows(loaded);
    } catch (error) {
      if (!isCancelled()) console.error('Error loading pending receipts:', error);
    }
  }, []);

  useEffect(() => {
    // Guards against setting state after this instance has unmounted (e.g.
    // the employee closed the modal while the read was still in flight) —
    // same reasoning as the enrollment-check effect below.
    let cancelled = false;
    load(() => cancelled);
    return () => {
      cancelled = true;
    };
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await isEnrolled();
        if (!cancelled) setEnrolled(result);
      } catch (error) {
        console.error('Error checking enrollment state:', error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleRetryAll = async () => {
    setBusy(true);
    setStatus(null);
    try {
      const result = await syncPendingReceipts();
      setStatus(describeResult(result));
      await load();
    } catch (error) {
      console.error('Error sending pending receipts:', error);
      setStatus({ text: 'Could not send. Please try again.', kind: 'error' });
    } finally {
      setBusy(false);
    }
  };

  /**
   * A stuck row's error text is the only diagnostic an employee (or the
   * office, over the phone) has — clearing it on a tap that did not actually
   * attempt anything would erase that for free. So this drains FIRST, while
   * the row is still flagged and therefore excluded from that drain, and
   * only clears the flag — making the row eligible — once the drain result
   * confirms one genuinely ran (no `skipped`). A second drain then makes the
   * real attempt. Two drains cost a little more than one, but a drain that
   * skips (unenrolled / already in flight) is cheap: it does no network work
   * at all for an unenrolled device, and the reentrant case means another
   * drain is already covering the queue anyway.
   */
  const handleRetry = async (id: number) => {
    setBusy(true);
    setRetryingId(id);
    setStatus(null);
    try {
      const probe = await syncPendingReceipts();
      if (probe.skipped) {
        setStatus(describeResult(probe));
        return;
      }
      await retryReceipt(id);
      const result = await syncPendingReceipts();
      setStatus(describeResult(result));
    } catch (error) {
      console.error('Error retrying receipt:', error);
      setStatus({ text: 'Could not retry. Please try again.', kind: 'error' });
    } finally {
      setRetryingId(null);
      setBusy(false);
      await load();
    }
  };

  const handleDiscard = async (id: number) => {
    // A hard delete of the only surviving copy of a paper receipt the
    // employee has likely already thrown away — irreversible, so it is
    // confirmed rather than one accidental tap away.
    if (!window.confirm('Discard this receipt? This cannot be undone — the photo will be lost.')) {
      return;
    }
    try {
      await discardReceipt(id);
    } catch (error) {
      console.error('Error discarding receipt:', error);
      setStatus({ text: 'Could not discard the receipt. Please try again.', kind: 'error' });
      return;
    }
    await load();
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <h2>Receipts waiting to send</h2>

        {status && <div className={`receipt-status receipt-status-${status.kind}`}>{status.text}</div>}

        {rows.length === 0 ? (
          <p className="receipt-subtitle">Nothing waiting to send.</p>
        ) : (
          <>
            <p className="receipt-subtitle">
              {enrolled === false
                ? "This phone isn't set up with the office yet — receipts stay here until it is."
                : 'These are saved on this phone and will send themselves when there is signal.'}
            </p>
            <ul className="receipt-pending-list">
              {rows.map((row) => (
                <li key={row.id} className="receipt-pending-item">
                  <div>
                    <strong>{row.vendor}</strong> <span>${(row.amountCents / 100).toFixed(2)}</span>
                    <div className="receipt-subtitle">
                      {row.txnDate}
                      {row.jobName ? ` · ${row.jobName}` : ''}
                    </div>
                    {(row.needsAttention === 1 || row.lastError) && (
                      // Render lastError whenever it is set, not only when
                      // needsAttention flags the row (review finding): a
                      // retryable failure — e.g. a 403 from a revoked
                      // enrollment code, which retries forever and never
                      // sets needsAttention — was previously reported to the
                      // employee only as "still no connection" from the
                      // footer, with no per-row diagnostic at all. The muted
                      // info style keeps "will retry on its own" visually
                      // distinct from the red "needs your action" banner.
                      <div
                        className={`receipt-status ${
                          row.needsAttention === 1 ? 'receipt-status-error' : 'receipt-status-info'
                        }`}
                      >
                        {row.needsAttention === 1
                          ? row.lastError || 'The dashboard would not accept this receipt.'
                          : `Will retry automatically — ${row.lastError}`}
                      </div>
                    )}
                  </div>
                  <div className="receipt-pending-actions">
                    {row.needsAttention === 1 && (
                      <button
                        type="button"
                        className="receipt-retry-btn"
                        disabled={retryingId === row.id}
                        onClick={() => handleRetry(row.id!)}
                      >
                        {retryingId === row.id ? 'Retrying…' : 'Retry'}
                      </button>
                    )}
                    <button type="button" className="receipt-discard-btn" onClick={() => handleDiscard(row.id!)}>
                      Discard
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </>
        )}

        <div className="modal-buttons-vertical">
          <button
            type="button"
            className="save-btn"
            onClick={handleRetryAll}
            disabled={busy || rows.length === 0 || enrolled === false}
          >
            {busy ? 'Sending…' : 'Try sending now'}
          </button>
          <button type="button" className="cancel-btn" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default PendingReceipts;
